import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../config/api";

// Course notifications are hints only: fetch through the authorized HTTP route
// before showing any content. Never acknowledge a message from a popup.
export default function useIncomingCourseMessages({ enabled, role, readerId, onMessage, onViewed }) {
  const { pathname } = useLocation();
  const current = useRef({ pathname, onMessage, onViewed });
  current.current = { pathname, onMessage, onViewed };
  const activeCourse = () => {
    if (document.visibilityState === "hidden" || !document.hasFocus()) return null;
    const pattern = role === "writer" ? /^\/writer\/courses\/(COURSE-[1-9]\d{4,})\/?$/ : /^\/courses\/(COURSE-[1-9]\d{4,})\/messages\/?$/;
    return current.current.pathname.match(pattern)?.[1] || null;
  };
  const activeRef = useRef(activeCourse);
  activeRef.current = activeCourse;

  useEffect(() => {
    if (!enabled || !readerId) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    let disposed = false;
    const seen = new Set();
    const pending = new Set();
    const remember = key => { seen.add(key); if (seen.size > 500) seen.delete(seen.values().next().value); };
    const validSession = () => !disposed && localStorage.getItem("token") === token;
    const changed = async event => {
      const { kind, courseId, messageId } = event.detail || {};
      if (!validSession() || kind !== "message" || !/^COURSE-[1-9]\d{4,}$/.test(courseId || "") || !/^[a-f\d]{24}$/i.test(messageId || "")) return;
      const key = `${courseId}:${messageId}`;
      if (seen.has(key) || pending.has(key)) return;
      if (activeRef.current() === courseId) { remember(key); return; }
      pending.add(key);
      try {
        const base = role === "writer" ? `/courses/writer/${courseId}` : `/courses/${courseId}`;
        const response = await api.get(`${base}/messages/${messageId}`);
        const message = response.data?.data;
        if (!validSession()) return;
        remember(key);
        if (activeRef.current() === courseId || !message || String(message._id) !== messageId || String(message.sender) === String(readerId) || message.readBy?.some(receipt => receipt.role === role)) return;
        const senderLabel = { customer: "Customer", writer: "Writer", admin: "Support" }[message.senderRole];
        if (!senderLabel) return;
        current.current.onMessage({ type: "course", key, courseId, courseBase: base, courseRoute: role === "writer" ? `/writer/courses/${courseId}` : `/courses/${courseId}/messages`, senderLabel,
          content: message.content?.trim().slice(0, 160) || (message.attachments?.length ? "New file shared" : "New message received"), createdAt: message.createdAt });
      } catch (_) { /* The unread inbox remains authoritative when access or a request fails. */ }
      finally { pending.delete(key); }
    };
    window.addEventListener("course-unread-changed", changed);
    return () => { disposed = true; window.removeEventListener("course-unread-changed", changed); };
  }, [enabled, role, readerId]);

  useEffect(() => {
    if (!enabled) return;
    const viewed = () => {
      const courseId = activeRef.current();
      if (courseId) current.current.onViewed(courseId);
    };
    viewed();
    window.addEventListener("focus", viewed);
    document.addEventListener("visibilitychange", viewed);
    return () => { window.removeEventListener("focus", viewed); document.removeEventListener("visibilitychange", viewed); };
  }, [enabled, pathname]);
}
